import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Container, Breadcrumbs, Link, IconButton, CircularProgress, useTheme, Divider } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from '../../MiniBot';
import { useAuth } from '../../../helpers/AuthProvider';

const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
    const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}</Typography>
        </Box>
    );
};
// --- Interfaces for Data Pipeline ---
interface ApplicationData {
    id: string;
    applicationName: string;
}

interface SolutionOwnerData {
    id: string;
    name: string;
}

interface DataPipelineDetails {
    projectId: string;
    networkProjectId: string;
    region: string;
    environmentSize: string;
    network: string;
    subNetwork: string;
    storageBucket: string;
    serviceAccount: string;
    podRange: string;
    serviceRange: string;
}

interface DataPipelineData {
    id: string;
    name: string;
    solutionType: string;
    solutionCategoryType: string;
    reasonForRejection: string;
    cloudProvider: 'gcp' | 'aws' | 'azure' | string;
    environment: string;
    details: {
        dataPipeline: DataPipelineDetails;
    };
    status: string;
    solutionOwner: SolutionOwnerData;
    application?: ApplicationData;
}

const DataPipelineDetailsPageContent: React.FC = () => {
    const navigate = useNavigate();
    const {id}  = useParams();
    const [dataPipeline, setDataPipeline] = useState<DataPipelineData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();
    const [dynamicLabels, setDynamicLabels] = useState({
        projectId: 'Project ID',
        networkProjectId: 'Network Project ID',
        network: 'Network',
        subNetwork: 'Sub-Network'
    });

    useEffect(() => {
        const fetchPipelineData = async () => {
            if (!id) {
                setIsLoading(false);
                setError("Pipeline ID is missing from URL.");
                return;
            }

            setIsLoading(true);
            setError(null);
            try {
                const query = `
                    query GetDataPipeline($getDataPipelineId: String!) {
                      getDataPipeline(id: $getDataPipelineId) {
                        id
                        name
                        solutionType
                        solutionCategoryType
                        reasonForRejection
                        cloudProvider
                        environment
                        status
                        details {
                          dataPipeline {
                            projectId
                            networkProjectId
                            region
                            environmentSize
                            network
                            subNetwork
                            storageBucket
                            serviceAccount
                            podRange
                            serviceRange
                          }
                        }
                        solutionOwner {
                          id
                          name
                        }
                        application {
                          id
                          applicationName
                        }
                      }
                    }
                `;
                const response = await queryGraphql(query, { getDataPipelineId: id });
                
                if (response.getDataPipeline) {
                    setDataPipeline(response.getDataPipeline);
                } else if (response.errors && response.errors.length > 0) {
                    setError(response.errors[0].message || "Error fetching data.");
                    setDataPipeline(null);
                } else {
                    setError("Data Pipeline not found or unexpected response structure.");
                    setDataPipeline(null);
                }
            } catch (err: any) {
                console.error("Error fetching Data Pipeline data:", err);
                setError(err.message || "Failed to fetch Data Pipeline details.");
                setDataPipeline(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPipelineData();
    }, [id]);
    
    useEffect(() => {
        if (dataPipeline?.cloudProvider) {
            switch (dataPipeline.cloudProvider) {
                case 'aws':
                    setDynamicLabels({
                        projectId: 'Account ID',
                        networkProjectId: 'VPC ID',
                        network: 'VPC',
                        subNetwork: 'Subnet'
                    });
                    break;
                case 'azure':
                    setDynamicLabels({
                        projectId: 'Subscription ID',
                        networkProjectId: 'Resource Group',
                        network: 'Virtual Network (VNet)',
                        subNetwork: 'Subnet'
                    });
                    break;
                case 'gcp':
                default:
                    setDynamicLabels({
                        projectId: 'Project ID',
                        networkProjectId: 'Network Project ID',
                        network: 'Network',
                        subNetwork: 'Sub-Network'
                    });
                    break;
            }
        }
    }, [dataPipeline]);


    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2 }}>Loading pipeline details...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Typography color="error" sx={{p:2}}>{error}</Typography>;
    }

    if (!dataPipeline) {
        return <Typography sx={{p:2}}>No pipeline data available.</Typography>;
    }

    const resourceOwnerName = dataPipeline.solutionOwner.name;

    const getStatusBackgroundColor = (status: string | undefined): string => {
        if (!status) return "#F8F8F8";
        const lowerStatus = status.toLowerCase();
        if (lowerStatus === "active" || lowerStatus === "approved") return "#12B76A10";
        if (lowerStatus === "rejected") return "#F0443810";
        if (lowerStatus === "pending") return "#FDB02210";
        return "#F8F8F8";
    };

    return (
        <Container maxWidth={false} sx={{ mt: 2, mb: 2, pl: { xs: 2, sm: 3 }, pr: {xs: 2, sm: 3} }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
                <Link underline="hover" color="inherit" href="/datasolutions/data-pipelines" sx={{fontFamily:'Gilroy-regular'}} onClick={(e) => { e.preventDefault(); navigate('/datasolutions/data-pipelines'); }}> Data Pipelines </Link>
                <Typography color="text.primary" sx={{fontSize: "14px", fontFamily:'Gilroy-Medium'}}>Pipeline Details</Typography>
            </Breadcrumbs>

            <Box display="flex" alignItems="center" mb={3}>
                <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/datasolutions/data-pipelines')}>
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold',fontSize: "18px"}}> Pipeline Details </Typography>
            </Box>

            <Box sx={{mx:0, boxShadow: "0px 3px 6px #10101029", borderRadius: "8px" }}>
                <Box sx={{
                    p: 2.5,
                    bgcolor: getStatusBackgroundColor(dataPipeline.status),
                    mb: 0,
                    mx: 0,
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                }}>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>
                                {dataPipeline.name}
                            </Typography>
                            {user.roles?.some((e: string) => ["cluster_owner"].includes(e)) && dataPipeline.status === "pending" && (
                                <IconButton sx={{ p: 0.5 }} edge="end" aria-label="edit" color="inherit" onClick={() => navigate(`/datasolutions/data-pipelines/edit/${id}`)}>
                                    <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit"/>
                                </IconButton>
                            )}
                        </Box>
                        <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>
                            Resource Owner: {resourceOwnerName}
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={3} sx={{p:3, pt: 2.5 }}>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>{dynamicLabels.projectId}</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dataPipeline.details.dataPipeline.projectId || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>{dynamicLabels.networkProjectId}</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dataPipeline.details.dataPipeline.networkProjectId || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Region</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dataPipeline.details.dataPipeline.region || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Application Name</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dataPipeline.application?.applicationName || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Category Type</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dataPipeline.solutionCategoryType || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Cloud Provider</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dataPipeline.cloudProvider?.toUpperCase() || 'N/A'}</Typography>
                    </Grid>
                    {dataPipeline.solutionCategoryType === 'composer' && (
                         <Grid item xs={12} sm={4} md={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Environment Size</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dataPipeline.details.dataPipeline.environmentSize || 'N/A'}</Typography>
                        </Grid>
                    )}
                </Grid>
            </Box>

              {dataPipeline.status === 'rejected' && (
                    <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow="0px 3px 6px #10101029">
                        <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                        <Divider sx={{ marginY: 2 }} />
                        <DetailItem label="Reason for Rejection" value={dataPipeline.reasonForRejection} />
                    </Box>
                )}

                {dataPipeline.status === 'active' && (
                    <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow="0px 3px 6px #10101029">
                        <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Cloud Infra Details</Typography>
                        <Divider sx={{ marginY: 2 }} />
                        <Grid container spacing={2.5}>
                            <Grid item xs={12} sm={6} md={3}><DetailItem label={dynamicLabels.network} value={dataPipeline.details.dataPipeline.network} /></Grid>
                            <Grid item xs={12} sm={6} md={3}><DetailItem label={dynamicLabels.subNetwork} value={dataPipeline.details.dataPipeline.subNetwork} /></Grid>
                            <Grid item xs={12} sm={6} md={3}><DetailItem label="Storage Bucket" value={dataPipeline.details.dataPipeline.storageBucket} /></Grid>
                            <Grid item xs={12} sm={6} md={3}><DetailItem label="Service Account" value={dataPipeline.details.dataPipeline.serviceAccount} /></Grid>
                            
                            {dataPipeline.cloudProvider === 'gcp' && (
                                <>
                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="Pod IP Address Range" value={dataPipeline.details.dataPipeline.podRange} /></Grid>
                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="Service IP Address Range" value={dataPipeline.details.dataPipeline.serviceRange} /></Grid>
                                </>
                            )}
                        </Grid>
                    </Box>
                )}
        </Container>
    );
};


const DataPipelineDetailsPage: React.FC = () => {
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') {
            localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
        }
    };

    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                index={"datasolutions-data-pipelines"}
                onStateChange={handleDrawerStateChange}
            />
            <MiniBot/>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    marginLeft: {xs: 0, sm: `${currentMarginLeft}px`},
                }}
            >
                <DataPipelineDetailsPageContent />
            </Box>
        </Box>
    );
};

export default DataPipelineDetailsPage;